import { useEffect } from 'react';
import { ulid } from 'ulid';

import { useDestinations } from '@/features/destinations/api/getDestinations';
import useInBranchPreselection from '@/hooks/useInBranchPreselection';
import useProceedWithDestination from '@/hooks/useProceedWithDestination';
import useAppointmentStore from '@/store/AppointmentStore';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import useSessionStore from '@/store/SessionStore';
import useThemeStore from '@/store/ThemeStore';
import { FlowType } from '@/types/FlowType';
import useAuthUserInfo from './useAuthUserInfo';
import useFeatureFlags from './useFeatureFlags';

// Idempotent: safe to mount from any landing route. SessionId is minted only
// when missing so HelpSelection → /search keeps analytics continuous.
const useLandingPageSetup = (flow: FlowType) => {
  useFeatureFlags();

  const { sessionId, setFlowType, setSessionId, setSessionSource } =
    useSessionStore();

  const setDestinations = useAppointmentStore((state) => state.setDestinations);
  const { isLuxuryTheme } = useThemeStore();
  const useNewDestinations = useFeatureFlagsStore(
    (state) => state.featureFlags.useNewDestinations,
  );
  const featureFlagsReady = useFeatureFlagsStore(
    (state) => state.featureFlagsReady,
  );

  const destinationsQuery = useDestinations(isLuxuryTheme, useNewDestinations, featureFlagsReady);
  useAuthUserInfo();
  useInBranchPreselection();

  const proceedWithDestination = useProceedWithDestination(flow);

  useEffect(() => {
    if (flow === FlowType.EMAIL) {
      setFlowType('emailInquiry');
    }
  }, [flow, setFlowType]);

  useEffect(() => {
    if (destinationsQuery.isSuccess && !sessionId) {
      setSessionId(ulid());
      setSessionSource('new');
    }
  }, [destinationsQuery.isSuccess, sessionId, setSessionId, setSessionSource]);

  useEffect(() => {
    if (destinationsQuery.data?.length) {
      setDestinations(destinationsQuery.data);
    }
  }, [destinationsQuery.data, setDestinations]);

  return {
    proceedWithDestination,
    destinationsQuery,
  };
};

export default useLandingPageSetup;
